import { useCallback, useEffect, useMemo } from 'react';

import { usePrevious } from '@adsk/alloy-react-helpers';

import { isGroupHeader } from '../helpers';
import { GenericItem, InputItemGroup, GroupHeader } from '../types';

function getGroupKeyForHighlightedIndex<TItem extends GenericItem>(
  menuItems: (GroupHeader | TItem)[],
  highlightedIndex: number,
): string | undefined {
  const highlightedElement = menuItems[highlightedIndex];
  if (!highlightedElement) {
    return undefined;
  }
  if (isGroupHeader(highlightedElement)) {
    return (highlightedElement as GroupHeader).groupKey as string;
  }
  for (let i = highlightedIndex - 1; i >= 0; i -= 1) {
    const menuItem = menuItems[i];
    if (menuItem && isGroupHeader(menuItem)) {
      return (menuItem as GroupHeader).groupKey as string;
    }
  }
  return undefined;
}

const useCollapsibleGroups = <TItem extends GenericItem>({
  allowCollapsibleGroups,
  highlightedIndex,
  menuItems,
  expandedGroupKey,
  setExpandedGroupKey,
}: {
  allowCollapsibleGroups?: boolean;
  maybeGroupedItems: TItem[] | InputItemGroup<TItem>[];
  highlightedIndex: number;
  menuItems: InputItemGroup<TItem>[] | undefined extends InputItemGroup<TItem>[]
    ? (GroupHeader | TItem)[]
    : TItem[];
  expandedGroupKey: string;
  setExpandedGroupKey: (
    value: ((prevState: string) => string) | string,
  ) => void;
}) => {
  const hasGroupHeaders = useMemo(
    () => allowCollapsibleGroups && menuItems.some(isGroupHeader),
    [menuItems, allowCollapsibleGroups],
  );
  const prevHighlightedIndex = usePrevious(highlightedIndex);

  const setExpandedGroup = useCallback(
    (key: string) => {
      setExpandedGroupKey((prev) => (prev === key ? '' : key));
    },
    [setExpandedGroupKey],
  );

  useEffect(() => {
    if (!allowCollapsibleGroups || !hasGroupHeaders || !menuItems) return;
    if (
      highlightedIndex === undefined ||
      highlightedIndex === null ||
      highlightedIndex === prevHighlightedIndex ||
      highlightedIndex < 0
    ) {
      return;
    }

    const groupKeyForHighlight = getGroupKeyForHighlightedIndex(
      menuItems,
      highlightedIndex,
    );
    if (
      groupKeyForHighlight !== undefined &&
      expandedGroupKey !== groupKeyForHighlight
    ) {
      setExpandedGroupKey(groupKeyForHighlight);
    }
  }, [
    allowCollapsibleGroups,
    highlightedIndex,
    menuItems,
    prevHighlightedIndex,
    expandedGroupKey,
    setExpandedGroupKey,
    hasGroupHeaders,
  ]);

  return {
    setExpandedGroup,
  };
};

export default useCollapsibleGroups;
